import { EventItem, Offer, PageConfig, PaymentMode } from '@tixa/schema'
import { AnalyticsEventsHandler } from './types'

import { isFee, isShippingOffer, hasInCart } from './utils'

const getPaymentTypeLabel = (mode: PaymentMode | undefined): string => {
  if (mode == null) return 'Credit Card'
  const labels: Record<PaymentMode, string> = {
    [PaymentMode.accountcredit]: 'Account Credit',
    [PaymentMode.card]: 'Credit Card',
    [PaymentMode.mkbszep]: 'MKB SZÉP',
    [PaymentMode.otpszep]: 'OTP SZÉP',
    [PaymentMode.knhszep]: 'K&H SZÉP',
    [PaymentMode.otopszepszallas]: 'OTP SZÉP Szállás',
    [PaymentMode.mkbszepszallas]: 'MKB SZÉP Szállás',
    [PaymentMode.free]: 'Free',
  }
  return labels[mode] ?? mode
}

const createGtagScript = (gtagId: string) => {
  const gtagScript = document.createElement('script')
  gtagScript.setAttribute('async', '')
  gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${gtagId}`
  document.head.append(gtagScript)
}

const createGtmScript = (gtmId: string) => {
  const gtmScript = document.createElement('script')
  gtmScript.text = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${gtmId}');`
  document.head.append(gtmScript)
}

const createCartAction = (offers: Offer[]) => {
  const gtagObj: GaActionData = {
    items: convertOffers(offers.filter(hasInCart)),
    shipping: offers
      .filter(isShippingOffer)
      .reduce((prev, curr) => prev + curr.price, 0),
    tax: offers.filter(isFee).reduce((prev, curr) => prev + curr.price, 0),
    value: offers
      .filter(hasInCart)
      .reduce(
        (prev, curr) => prev + curr.price * (curr.eligibleQuantity?.value ?? 1),
        0
      ),
  }
  const coupon = offers
    .filter((offer) => offer.appliedCoupon !== undefined)
    .map((offer) => offer.appliedCoupon)
    .reduce((prev, curr) => {
      return `${prev}${prev ? ' / ' : ''}${curr}`
    }, '')

  if (coupon && coupon.length > 0) {
    gtagObj.coupon = coupon
  }
  return gtagObj
}

const convertOffers = (offers: Offer[]): GaProduct[] => {
  return offers.map((offer) => {
    const descriptionValue = Array.isArray(offer.description)
      ? offer.description[0]
      : offer.description
    const obj: GaProduct = {
      item_id: offer.sku,
      item_name: offer['@id'],
      item_category: descriptionValue,
      quantity: offer.eligibleQuantity.value,
      price: offer.price,
      currency: offer.priceCurrency,
      item_variant: offer.category,
    }
    if (offer.appliedCoupon && offer.appliedCoupon.length > 0) {
      obj.coupon = offer.appliedCoupon
    }
    return obj
  })
}

const convertOffersToCheckoutItems = (offers: Offer[]) =>
  offers.filter(hasInCart).map((offer) => {
    const descriptionValue = Array.isArray(offer.description)
      ? offer.description[0]
      : offer.description
    return {
      item_id: offer.sku,
      item_name: offer.name,
      price: offer.price,
      item_brand: offer.name,
      item_category: descriptionValue,
      item_variant: offer.category || 'ticket',
      quantity: offer.eligibleQuantity?.value ?? 0,
    }
  })
interface Ga4SerpItem {
  item_id: string
  item_name: string
  price?: number
  item_brand?: string
  index: number
}

const convertSearchEventsToEcommerceItems = (
  events: EventItem[]
): Ga4SerpItem[] =>
  events.map((ev, i) => ({
    item_id: ev.identifier,
    item_name: ev.name,
    item_brand: ev.location?.name,
    index: i + 1,
  }))

const eventToSerpItem = (ev: EventItem, index: number): Ga4SerpItem => ({
  item_id: ev.identifier,
  item_name: ev.name,
  item_brand: ev.location?.name,
  index,
})

const createGoogleAnalyticsHandler = (): AnalyticsEventsHandler => {
  let isActive = false

  const propertyIds: string[] = []
  let conversionId: string

  const checkoutStartTime = new Date()
  const paymentStartTime = new Date()
  let queueStartTime = new Date()

  const getFinishTime = (startTime: Date) => {
    return Math.round((new Date().getTime() - startTime.getTime()) / 1000)
  }

  const engagement = (event: EngagementEvents, action?: GaActionData) => {
    const data = {
      ...action,
      send_to: propertyIds,
    }
    send('event', event, data)
  }

  const sendCustomEvent = (
    event: string,
    category: string,
    label: string,
    value?: number
  ) => {
    const data = {
      event_category: category,
      event_label: label,
      value,
      send_to: propertyIds,
    }
    send(event, 'event', data)
  }

  const send = (...args) => {
    if (isActive && 'gtag' in window) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      window.gtag(...args)
    }
  }

  return {
    init: async (config: PageConfig) => {
      if (config.ga) {
        const gaConf = config.ga
        isActive = true

        if (gaConf.gtag) {
          createGtmScript(gaConf.gtag)
        } else if (gaConf.trackingId) {
          createGtagScript(gaConf.trackingId)
        }

        if (gaConf.partner) {
          if (gaConf.partner.gtag) {
            createGtmScript(gaConf.partner.gtag)
          } else if (gaConf.partner.trackingId) {
            createGtagScript(gaConf.partner.trackingId)
          }
        }

        propertyIds.push(gaConf.trackingId)
        send('js', new Date())
        send('config', gaConf.trackingId) // GA_MEASUREMENT_ID

        if (gaConf.partner && gaConf.partner.trackingId) {
          send('config', gaConf.partner.trackingId)
          propertyIds.push(gaConf.partner.trackingId)
          if (gaConf.partner.conversionId) {
            conversionId = gaConf.partner.conversionId
          }
        }
      }
    },

    searched: (term: string) =>
      engagement(EngagementEvents.Search, { search_term: term }),

    viewSearchResultList: (term: string, events: EventItem[]) => {
      const date = new Date().toISOString().slice(0, 10)
      const itemListId = term || 'serp'
      const itemListName = `TIXA-SERP-${date}-${itemListId}`
      const payload = {
        ecommerce: {
          item_list_id: itemListId,
          item_list_name: itemListName,
          items: convertSearchEventsToEcommerceItems(events),
        },
        send_to: propertyIds,
      }
      send('event', 'view_item_list', payload)
    },

    selectSearchResultItem: (term: string, event: EventItem, index: number) => {
      const date = new Date().toISOString().slice(0, 10)
      const itemListId = term || 'serp'
      const itemListName = `TIXA-SERP-${date}-${itemListId}`
      const payload = {
        ecommerce: {
          item_list_id: itemListId,
          item_list_name: itemListName,
          items: [eventToSerpItem(event, index)],
        },
        send_to: propertyIds,
      }
      send('event', 'select_item', payload)
    },

    viewed: (offers: Offer[]) =>
      engagement(EngagementEvents.ViewItemList, {
        items: convertOffers(offers),
      }),

    addedToCart: (params: {
      name: string
      offerId: string
      ticketTypeName: string
      ticketTypeId: string
      offerPrice: number
      currency: string
      quantity: number
      coupon?: string
      type?: string
    }) => {
      const item = {
        item_id: params.ticketTypeId,
        item_name: params.name,
        price: params.offerPrice,
        item_brand: params.name,
        item_category: params.ticketTypeName,
        item_variant: params.type || 'ticket',
        quantity: params.quantity,
      }
      const payload = {
        ecommerce: { items: [item] },
        send_to: propertyIds,
      }
      send('event', EcommerceEvents.AddToCart, payload)
    },

    removedFromCart: (params: {
      name: string
      offerId: string
      ticketTypeName: string
      ticketTypeId: string
      offerPrice: number
      currency: string
      quantity: number
      type?: string
    }) => {
      const item = {
        item_id: params.ticketTypeId,
        item_name: params.name,
        price: params.offerPrice,
        item_brand: params.name,
        item_category: params.ticketTypeName,
        item_variant: params.type || 'ticket',
        quantity: params.quantity,
      }
      const payload = {
        ecommerce: { items: [item] },
        send_to: propertyIds,
      }
      send('event', EcommerceEvents.RemoveFromCart, payload)
    },

    selectPromotion: (params: {
      promotion_id: string
      promotion_name: string
      creative_name?: string
      creative_slot?: string
    }) => {
      const promotion = {
        promotion_id: params.promotion_id,
        promotion_name: params.promotion_name,
        creative_name: params.creative_name,
        creative_slot: params.creative_slot ?? 'Homepage Banner',
      }
      const payload = {
        ecommerce: { promotions: [promotion] },
        send_to: propertyIds,
      }
      send('event', 'select_promotion', payload)
    },

    checkoutStarted: (cartId: string, offers: Offer[]) => {
      const items = convertOffersToCheckoutItems(offers)
      const payload = {
        ecommerce: { items },
        send_to: propertyIds,
      }
      send('event', EcommerceEvents.BeginCheckout, payload)
    },

    checkoutExpired: () =>
      sendCustomEvent(
        'cart-expired',
        'cart',
        'CartExpired',
        getFinishTime(checkoutStartTime)
      ),

    paymentInfoAdded: (
      offers: Offer[],
      _data?: unknown,
      paymentMode?: PaymentMode
    ) => {
      const items = convertOffersToCheckoutItems(offers).map((i) => ({
        item_id: i.item_id,
        item_name: i.item_name,
        quantity: i.quantity,
      }))
      const payload = {
        ecommerce: {
          payment_type: getPaymentTypeLabel(paymentMode),
          items,
        },
        send_to: propertyIds,
      }
      send('event', EcommerceEvents.AddPaymentInfo, payload)
    },

    formInvalid: () =>
      sendCustomEvent('cart-form-invalid', 'cart', 'Form is invalid'),

    appliedCoupon: () =>
      sendCustomEvent('applied-coupon', 'event-box', 'Applied Coupon'),

    appliedCode: () =>
      sendCustomEvent('applied-code', 'event-box', 'Applied Code'),

    openedSeatmap: () =>
      sendCustomEvent('opened-seatmap', 'event-box', 'Opened Seatmap'),

    paymentStarted: () =>
      sendCustomEvent(
        'cart-payment-started',
        'cart',
        'Payment started',
        getFinishTime(checkoutStartTime)
      ),

    paymentFailure: () =>
      sendCustomEvent(
        'cart-payment-failure',
        'cart',
        'Payment failure',
        getFinishTime(paymentStartTime)
      ),

    purchased: (id: string, offers: Offer[]) => {
      const totals = createCartAction(offers)
      const items = convertOffersToCheckoutItems(offers)
      const payload = {
        ecommerce: {
          transaction_id: id,
          affiliation: 'TIXA.hu',
          value: totals.value,
          tax: totals.tax,
          shipping: totals.shipping,
          currency: offers[0]?.priceCurrency ?? 'HUF',
          items,
          ...(totals.coupon ? { coupon: totals.coupon } : {}),
        },
        send_to: propertyIds,
      }
      send('event', EcommerceEvents.Purchase, payload)

      if (conversionId) {
        send('event', 'conversion', {
          send_to: conversionId,
          value: totals.value,
          currency: offers[0]?.priceCurrency,
        })
      }
    },

    registered: (value: number) =>
      sendCustomEvent('cart-registered', 'cart', 'Registered in cart', value),

    closePage: () =>
      sendCustomEvent('cart-closed-page', 'cart', 'Closed page in cart'),

    gotIntoQueue: () => {
      queueStartTime = new Date()
      sendCustomEvent('queue-start', 'queue', 'Start queue')
    },

    leftQueue: () =>
      sendCustomEvent(
        'queue-left',
        'queue',
        'Left queue',
        getFinishTime(queueStartTime)
      ),

    finishQueue: () =>
      sendCustomEvent(
        'queue-finished',
        'queue',
        'Queue finished',
        getFinishTime(queueStartTime)
      ),

    queueSoldout: () =>
      sendCustomEvent(
        'queue-soldout',
        'queue',
        'Queue sold out',
        getFinishTime(queueStartTime)
      ),
  }
}

export { createGoogleAnalyticsHandler }

enum EngagementEvents {
  Login = 'login',
  Search = 'search',
  SelectContent = 'select_content',
  Share = 'share',
  SignUp = 'sign_up',
  ViewItem = 'view_item',
  ViewItemList = 'view_item_list',
  ViewPromotion = 'view_promotion',
  ViewSearchResults = 'view_search_results',
}

enum EcommerceEvents {
  AddPaymentInfo = 'add_payment_info',
  AddToCart = 'add_to_cart',
  AddToWishlist = 'add_to_wishlist',
  BeginCheckout = 'begin_checkout',
  CheckoutProgress = 'checkout_progress',
  GenerateLead = 'generate_lead',
  Purchase = 'purchase',
  Refund = 'refund',
  RemoveFromCart = 'remove_from_cart',
  SetCheckoutOption = 'set_checkout_option',
}

interface GaActionData {
  /** When action type is purchase or refund **/
  id?: string
  transaction_id?: string
  affiliation?: string
  value?: number
  tax?: number
  shipping?: number
  items?: GaProduct[]
  coupon?: string
  send_to?: string[]
  /** Search event **/
  search_term?: string
}

interface GaProduct {
  item_id: string
  item_name: string
  item_category: string
  item_category2?: string
  quantity?: number
  price?: number
  currency?: string
  coupon?: string
  item_variant?: string
}
